import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';

const MockTest = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const startMockTest = async (testType) => {
    setLoading(true);
    setError('');
    
    try {
      let sessionResponse;
      let sessionId;
      
      try {
        sessionResponse = await apiService.startMockTest();
        sessionId = sessionResponse.session_id;
        console.log('Mock test session started:', sessionId);
      } catch (mockError) {
        console.error('Error starting mock test session:', mockError);
        throw new Error('Failed to start mock test session');
      }

      if (!sessionId) {
        throw new Error('No session ID received from server');
      }

      let firstQuestion;
      try {
        firstQuestion = await apiService.getNextMockQuestion(sessionId);
        console.log('First mock test question received:', firstQuestion);
      } catch (questionError) {
        console.error('Error fetching first mock question:', questionError);
        throw new Error('Could not fetch questions for the mock test');
      }

      if (firstQuestion) {
        navigate('/question', { 
          state: { 
            questions: [firstQuestion], 
            sessionLength: testType === 'full' ? 50 : 20,
            difficulty: 'mixed',
            isMockTest: true,
            sessionId: sessionId,
            testType: testType
          } 
        });
      } else {
        throw new Error('Could not fetch the first question for the mock test');
      }
    } catch (error) {
      console.error('Error starting mock test:', error);
      setError(`Failed to start mock test: ${error.message}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const startFullLengthTest = () => {
    startMockTest('full');
  };

  const navigateToMockProgress = () => {
    navigate('/mock-progress');
  };

  return (
    <div style={styles.container}>
      <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" rel="stylesheet" />
      
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.titleSection}>
            <h1 style={styles.title}>Mock Test</h1>
            <p style={styles.subtitle}>Simulate real exam conditions with timed tests</p>
          </div>
        </div>
      </div>

      <div style={styles.divider}></div>

      <div style={styles.section}>
        <div style={styles.content}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>Take a Mock Test</h2>
              <button 
                style={styles.progressButton}
                onClick={navigateToMockProgress}
              >
                <i className="fas fa-chart-bar" style={styles.buttonIcon}></i>
                View Progress
              </button>
            </div>
            <p style={styles.cardText}>
              Experience exam-like conditions with timed tests that help you prepare 
              for the actual examination environment. All questions are fetched from 
              the mock test API to simulate real exam questions.
            </p>
            
            {error && (
              <div style={styles.errorAlert}>
                <i className="fas fa-exclamation-circle" style={styles.errorIcon}></i>
                <span style={styles.errorText}>{error}</span>
              </div>
            )}

            <div style={styles.testOptions}>
              <div style={styles.option}>
                <div style={styles.optionHeader}>
                  <i className="fas fa-graduation-cap" style={styles.optionIcon}></i>
                  <h3 style={styles.optionTitle}>Full Length Test</h3>
                </div>
                <div style={styles.optionDetails}>
                  <p style={styles.optionDescription}>
                    Complete 50 questions in 90 minutes - Simulates the full exam experience
                  </p>
                
                </div>
                <button 
                  style={{
                    ...styles.startButton,
                    ...(loading ? styles.startButtonDisabled : {})
                  }}
                  onClick={startFullLengthTest}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div style={styles.spinner}></div>
                      Starting Test...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-play" style={styles.buttonIcon}></i>
                      Start Full Test
                    </>
                  )}
                </button>
              </div>
              
            
            </div>

            
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
    padding: '20px',
    fontFamily: '"Open Sans", sans-serif',
  },
  header: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '30px',
    marginBottom: '25px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    border: '1px solid #e0e0e0'
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  titleSection: {
    flex: 1
  },
  title: {
    fontSize: '32px',
    fontWeight: '600',
    margin: '0 0 8px 0',
    color: '#2c5aa0',
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0px',
    lineHeight: '1.2'
  },
  subtitle: {
    fontSize: '16px',
    margin: '0',
    color: '#666666',
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0.25px',
    lineHeight: '1.5'
  },
  divider: {
    height: '1px',
    backgroundColor: '#e0e0e0',
    margin: '20px 0',
    maxWidth: '1200px',
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  section: {
    maxWidth: '1200px',
    margin: '0 auto 30px auto'
  },
  content: {
    maxWidth: '1000px',
    margin: '0 auto'
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '40px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e0e0e0'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  cardTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#2c5aa0',
    marginBottom: '16px',
    fontFamily: '"Open Sans", sans-serif',
  },
  progressButton: {
    backgroundColor: '#ffc107',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#212529',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: '"Open Sans", sans-serif',
  },
  cardText: {
    fontSize: '16px',
    color: '#666666',
    lineHeight: '1.6',
    marginBottom: '30px',
    fontFamily: '"Open Sans", sans-serif',
  },
  errorAlert: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  errorIcon: {
    color: '#dc2626',
    fontSize: '16px',
  },
  errorText: {
    color: '#dc2626',
    fontSize: '14px',
    fontWeight: '500',
  },
  testOptions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '30px',
    marginBottom: '40px',
  },
  option: {
    padding: '30px',
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
    border: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    transition: 'all 0.3s ease',
  },
  optionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '20px',
  },
  optionIcon: {
    color: '#2c5aa0',
    fontSize: '32px',
  },
  optionTitle: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#2c5aa0',
    margin: 0,
    fontFamily: '"Open Sans", sans-serif',
  },
  optionDetails: {
    flex: 1,
    marginBottom: '25px',
  },
  optionDescription: {
    fontSize: '15px',
    color: '#666666',
    lineHeight: '1.5',
    marginBottom: '20px',
    fontFamily: '"Open Sans", sans-serif',
  },

  optionFeatures: {
    fontSize: '14px',
    color: '#666666',
    padding: '8px 0',
    fontFamily: '"Open Sans", sans-serif',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  startButton: {
    backgroundColor: '#2c5aa0',
    border: 'none',
    borderRadius: '8px',
    padding: '16px 24px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontFamily: '"Open Sans", sans-serif',
    width: '100%',
  },
  startButtonDisabled: {
    backgroundColor: '#a0a8ac',
    cursor: 'not-allowed',
  },
  buttonIcon: {
    fontSize: '16px',
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid transparent',
    borderTop: '2px solid #ffffff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  infoSection: {
    padding: '25px',
    backgroundColor: '#e3f2fd',
    borderRadius: '8px',
    border: '1px solid #bbdefb',
  },
  infoTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#2c5aa0',
    marginBottom: '15px',
    fontFamily: '"Open Sans", sans-serif',
    display: 'flex',
    alignItems: 'center',
  },
  infoContent: {
    fontSize: '15px',
    color: '#374151',
  },
  infoText: {
    marginBottom: '20px',
    lineHeight: '1.6',
  },
  featureList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
  },
  featureIcon: {
    color: '#2c5aa0',
    fontSize: '16px',
  },
};

export default MockTest;